import type { Locale } from "@/translations";
import { pathnameToCanonical, withLocalePrefix } from "@/lib/i18nRouting";

/** Empty numeric slot in listing filter blobs. */
export const LISTING_PRICE_EMPTY = "-";

/** Canonical token when no category filter is applied. */
export const LISTING_ALL_TOKEN = "all";

/**
 * Between logical filter slots (category / sort / …). Not used inside slugified names
 * (those use single `-`). Decode still accepts legacy blobs joined with `__`.
 */
export const LISTING_SLOT_SEPARATOR = "+";

const LEGACY_LISTING_SLOT_SEPARATOR = "__";

/**
 * When category is omitted from the URL, a single path segment is interpreted as sort
 * only if it matches one of these tokens (otherwise it is treated as a category slug).
 */
export const LISTING_GLOBAL_SORT_TOKENS = new Set<string>([
  "popular",
  "price-low",
  "price-high",
  "rating",
  "savings",
  "newest",
  "oldest",
  "expiring",
  "discount-high",
  "discount-low",
]);

function blobHasMultipleSlots(raw: string): boolean {
  return (
    raw.includes(LISTING_SLOT_SEPARATOR) ||
    raw.includes(LEGACY_LISTING_SLOT_SEPARATOR)
  );
}

function decodeURIComponentSafe(raw: string): string {
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

/**
 * Lowercase path token for categories/labels: spaces → hyphens, `&` → `and`,
 * strips accents; **no** URI encoding (safe ASCII subset).
 */
export function slugifyListingLabel(raw: string | undefined | null): string {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s) return "";
  return s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 96);
}

export type FourSlotListingFilters = {
  categoryId: string;
  sortBy: string;
  priceMin: string;
  priceMax: string;
};

export type TwoSlotListingFilters = {
  categoryId: string;
  sortBy: string;
};

function trimTrailingDefaults(
  segments: string[],
  defaults: readonly string[],
): string[] {
  let n = segments.length;
  while (
    n > 0 &&
    n <= defaults.length &&
    segments[n - 1] === defaults[n - 1]
  ) {
    n -= 1;
  }
  return segments.slice(0, n);
}

/** Four ordered slots joined with {@link LISTING_SLOT_SEPARATOR} into one path segment. */
function joinListingBlob(parts: string[]): string {
  return parts.join(LISTING_SLOT_SEPARATOR);
}

function splitListingBlob(blob: string): string[] {
  if (!blob) return [];
  if (blob.includes(LISTING_SLOT_SEPARATOR)) {
    return blob.split(LISTING_SLOT_SEPARATOR);
  }
  if (blob.includes(LEGACY_LISTING_SLOT_SEPARATOR)) {
    return blob.split(LEGACY_LISTING_SLOT_SEPARATOR);
  }
  return [blob];
}

/** Digits (and optional decimal) for URL payload — strips commas. */
export function normalizeListingPriceDigits(value: string): string {
  const t = value.trim().replace(/,/g, "");
  if (!t) return "";
  const n = Number(t);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

/**
 * Map path-decoded min/max (from `min-` / `max-` tokens, legacy slots, or query params)
 * to values for controlled price inputs.
 */
export function mapListingDecodedPriceToInput(
  raw: string | undefined | null,
): string {
  if (raw == null) return "";
  const t = String(raw).trim();
  if (!t) return "";
  const n = Number(t.replace(/,/g, ""));
  if (!Number.isFinite(n)) return t;
  return String(n);
}

/**
 * When both min and max are set numerically but reversed, swaps so min ≤ max for
 * URL encoding and filtering.
 */
export function normalizeListingPricePairForFilterAndUrl(
  minStr: string,
  maxStr: string,
): { priceMin: string; priceMax: string } {
  const minT = normalizeListingPriceDigits(minStr);
  const maxT = normalizeListingPriceDigits(maxStr);
  if (!minT && !maxT) return { priceMin: "", priceMax: "" };
  if (minT && !maxT) return { priceMin: minT, priceMax: "" };
  if (!minT && maxT) return { priceMin: "", priceMax: maxT };
  const minN = Number(minT);
  const maxN = Number(maxT);
  if (minN <= maxN) return { priceMin: minT, priceMax: maxT };
  return { priceMin: maxT, priceMax: minT };
}

/**
 * Merges live inputs with debounced copies for URL/filter sync.
 * If the user cleared a field (live empty) but debounce still holds an old value, live wins.
 * If debounce has not flushed yet (debounced empty) but live has URL-hydrated values, live wins.
 */
export function effectiveListingPricesForSync(
  filtersReadyForUrlSync: boolean,
  liveMin: string,
  liveMax: string,
  debouncedMin: string,
  debouncedMax: string,
): { priceMin: string; priceMax: string } {
  if (!filtersReadyForUrlSync) {
    return normalizeListingPricePairForFilterAndUrl(liveMin, liveMax);
  }
  const liveMinT = liveMin.trim();
  const liveMaxT = liveMax.trim();
  const debMinT = debouncedMin.trim();
  const debMaxT = debouncedMax.trim();

  const minRaw =
    liveMinT === "" && debMinT !== ""
      ? ""
      : debMinT !== ""
        ? debouncedMin
        : liveMin;
  const maxRaw =
    liveMaxT === "" && debMaxT !== ""
      ? ""
      : debMaxT !== ""
        ? debouncedMax
        : liveMax;

  return normalizeListingPricePairForFilterAndUrl(minRaw, maxRaw);
}

function encodePriceSlotPrefixed(
  value: string,
  role: "min" | "max",
): string {
  const n = normalizeListingPriceDigits(value);
  if (!n) return LISTING_PRICE_EMPTY;
  return `${role}-${n}`;
}

function decodePricePrefixedToken(
  token: string,
): { role: "min" | "max"; value: string } | null {
  const m = /^(min|max)-(.*)$/i.exec(token.trim());
  if (!m) return null;
  const body = (m[2] ?? "").trim();
  if (!body) return null;
  return { role: m[1].toLowerCase() as "min" | "max", value: body };
}

function isLegacyBarePriceSlot(token: string): boolean {
  const t = token.trim();
  if (t === LISTING_PRICE_EMPTY || t === "") return true;
  return /^-?\d+(\.\d+)?$/.test(t.replace(/,/g, ""));
}

/**
 * Builds path tokens without `-` placeholders or default sort/category noise:
 * `[category?][sort?][min-*?][max-*?]` — only segments that differ from defaults.
 */
function encodeFourSlotParts(
  filters: FourSlotListingFilters,
  sortDefault: string,
): string[] {
  const cat = filters.categoryId.trim() || LISTING_ALL_TOKEN;
  const sort = filters.sortBy.trim() || sortDefault;
  const minT = encodePriceSlotPrefixed(filters.priceMin, "min");
  const maxT = encodePriceSlotPrefixed(filters.priceMax, "max");

  const parts: string[] = [];
  if (cat !== LISTING_ALL_TOKEN) {
    parts.push(cat);
  }
  if (sort !== sortDefault) {
    parts.push(sort);
  }
  if (minT !== LISTING_PRICE_EMPTY) {
    parts.push(minT);
  }
  if (maxT !== LISTING_PRICE_EMPTY) {
    parts.push(maxT);
  }
  return parts;
}

/**
 * Path segments for the optional catch-all (typically **one** segment = full blob).
 */
export function encodeFourSlotListingSegments(
  filters: FourSlotListingFilters,
  sortDefault = "popular",
): string[] {
  const parts = encodeFourSlotParts(filters, sortDefault);
  if (parts.length === 0) return [];
  return [joinListingBlob(parts)];
}

function decodeFourSlotFromParts(
  parts: string[],
  sortDefault: string,
): FourSlotListingFilters {
  const raw = parts.map((p) => decodeURIComponentSafe(p.trim()));

  let priceMin = "";
  let priceMax = "";
  const prefixedRest: string[] = [];

  for (const p of raw) {
    const dec = decodePricePrefixedToken(p);
    if (dec) {
      if (dec.role === "min") priceMin = dec.value;
      else priceMax = dec.value;
      continue;
    }
    prefixedRest.push(p);
  }

  let rest = prefixedRest.filter((p) => {
    const t = p.trim();
    return t !== "" && t !== LISTING_PRICE_EMPTY;
  });

  if (
    priceMin === "" &&
    priceMax === "" &&
    rest.length === 4 &&
    isLegacyBarePriceSlot(rest[2] ?? "") &&
    isLegacyBarePriceSlot(rest[3] ?? "")
  ) {
    const p2 = (rest[2] ?? "").trim();
    const p3 = (rest[3] ?? "").trim();
    if (p2 !== LISTING_PRICE_EMPTY) priceMin = p2.replace(/,/g, "");
    if (p3 !== LISTING_PRICE_EMPTY) priceMax = p3.replace(/,/g, "");
    rest = rest.slice(0, 2);
  } else if (
    priceMin === "" &&
    priceMax === "" &&
    rest.length === 3 &&
    isLegacyBarePriceSlot(rest[2] ?? "")
  ) {
    const p2 = (rest[2] ?? "").trim();
    if (p2 !== LISTING_PRICE_EMPTY) priceMin = p2.replace(/,/g, "");
    rest = rest.slice(0, 2);
  }

  if (rest.length === 0) {
    return {
      categoryId: LISTING_ALL_TOKEN,
      sortBy: sortDefault,
      priceMin,
      priceMax,
    };
  }

  if (rest.length === 1) {
    const t = rest[0] ?? "";
    if (t === LISTING_ALL_TOKEN || t === "all") {
      return {
        categoryId: LISTING_ALL_TOKEN,
        sortBy: sortDefault,
        priceMin,
        priceMax,
      };
    }
    if (LISTING_GLOBAL_SORT_TOKENS.has(t)) {
      return {
        categoryId: LISTING_ALL_TOKEN,
        sortBy: t,
        priceMin,
        priceMax,
      };
    }
    return {
      categoryId: t,
      sortBy: sortDefault,
      priceMin,
      priceMax,
    };
  }

  if (rest.length === 2) {
    const a = rest[0] ?? "";
    const b = rest[1] ?? "";
    if (
      (a === LISTING_ALL_TOKEN || a === "all") &&
      LISTING_GLOBAL_SORT_TOKENS.has(b)
    ) {
      return {
        categoryId: LISTING_ALL_TOKEN,
        sortBy: b,
        priceMin,
        priceMax,
      };
    }
    return {
      categoryId: a,
      sortBy: b,
      priceMin,
      priceMax,
    };
  }

  return {
    categoryId: rest[0] ?? LISTING_ALL_TOKEN,
    sortBy: rest[1] ?? sortDefault,
    priceMin,
    priceMax,
  };
}

/** Legacy: multiple URL segments (slash-separated). */
function decodeFourSlotLegacySlashSegments(
  segments: string[],
  sortDefault: string,
): FourSlotListingFilters {
  const s = segments.map((x) => decodeURIComponentSafe(x.trim()));
  return decodeFourSlotFromParts(
    [s[0] ?? "", s[1] ?? "", s[2] ?? "", s[3] ?? ""],
    sortDefault,
  );
}

/**
 * Decode filters from catch-all segments (blob or legacy slash-separated UUIDs/slugs).
 */
export function decodeFourSlotListingFilters(
  segments: string[] | undefined,
  sortDefault = "popular",
): FourSlotListingFilters {
  const s = segments ?? [];
  if (s.length === 0) {
    return decodeFourSlotFromParts([], sortDefault);
  }

  if (s.length === 1) {
    const raw = decodeURIComponentSafe(s[0]);
    if (blobHasMultipleSlots(raw)) {
      return decodeFourSlotFromParts(splitListingBlob(raw), sortDefault);
    }
    return decodeFourSlotFromParts([raw], sortDefault);
  }

  return decodeFourSlotLegacySlashSegments(s, sortDefault);
}

const DEFAULTS_OFFERS: readonly string[] = [
  LISTING_ALL_TOKEN,
  "newest",
];

export function encodeOffersListingSegments(
  filters: TwoSlotListingFilters,
): string[] {
  const full = [
    filters.categoryId.trim() || LISTING_ALL_TOKEN,
    filters.sortBy.trim() || "newest",
  ];
  const parts = trimTrailingDefaults(full, DEFAULTS_OFFERS);
  if (parts.length > 0 && parts[0] === LISTING_ALL_TOKEN) {
    const tail = parts.slice(1);
    return tail.length === 0 ? [] : [joinListingBlob(tail)];
  }
  if (parts.length === 0) return [];
  return [joinListingBlob(parts)];
}

export function decodeOffersListingFilters(
  segments: string[] | undefined,
): TwoSlotListingFilters {
  const s = segments ?? [];
  if (s.length === 0) {
    return { categoryId: LISTING_ALL_TOKEN, sortBy: "newest" };
  }
  if (s.length === 1) {
    const raw = decodeURIComponentSafe(s[0]);
    if (blobHasMultipleSlots(raw)) {
      const parts = splitListingBlob(raw);
      return decodeTwoSlotFromParts(parts, "newest");
    }
    return decodeTwoSlotFromParts([raw], "newest");
  }
  const dec = decodeURIComponentSafe(s[0] ?? "");
  const dec1 = decodeURIComponentSafe(s[1] ?? "");
  return decodeTwoSlotFromParts([dec, dec1], "newest");
}

function decodeTwoSlotFromParts(
  parts: string[],
  sortDefault: string,
): TwoSlotListingFilters {
  const rest = parts.map((p) => decodeURIComponentSafe(p.trim())).filter(Boolean);
  if (rest.length === 0) {
    return { categoryId: LISTING_ALL_TOKEN, sortBy: sortDefault };
  }
  if (rest.length === 1) {
    const t = rest[0] ?? "";
    if (t === LISTING_ALL_TOKEN || t === "all") {
      return { categoryId: LISTING_ALL_TOKEN, sortBy: sortDefault };
    }
    if (LISTING_GLOBAL_SORT_TOKENS.has(t)) {
      return { categoryId: LISTING_ALL_TOKEN, sortBy: t };
    }
    return { categoryId: t, sortBy: sortDefault };
  }
  return {
    categoryId: rest[0] ?? LISTING_ALL_TOKEN,
    sortBy: rest[1] ?? sortDefault,
  };
}

const DEFAULTS_NEWS: readonly string[] = [LISTING_ALL_TOKEN, "newest"];

export function encodeNewsListingSegments(
  filters: TwoSlotListingFilters,
): string[] {
  const full = [
    filters.categoryId.trim() || LISTING_ALL_TOKEN,
    filters.sortBy.trim() || "newest",
  ];
  const parts = trimTrailingDefaults(full, DEFAULTS_NEWS);
  if (parts.length > 0 && parts[0] === LISTING_ALL_TOKEN) {
    return parts.slice(1).length === 0
      ? []
      : [joinListingBlob(parts.slice(1))];
  }
  if (parts.length === 0) return [];
  return [joinListingBlob(parts)];
}

export function decodeNewsListingFilters(
  segments: string[] | undefined,
): TwoSlotListingFilters {
  const s = segments ?? [];
  if (s.length === 0) {
    return { categoryId: LISTING_ALL_TOKEN, sortBy: "newest" };
  }
  if (s.length === 1) {
    const raw = decodeURIComponentSafe(s[0]);
    if (blobHasMultipleSlots(raw)) {
      const parts = splitListingBlob(raw);
      return decodeTwoSlotFromParts(parts, "newest");
    }
    return decodeTwoSlotFromParts([raw], "newest");
  }
  return decodeTwoSlotFromParts(
    [
      decodeURIComponentSafe(s[0] ?? ""),
      decodeURIComponentSafe(s[1] ?? ""),
    ],
    "newest",
  );
}

/**
 * Canonical listing path (no locale prefix): `/base` or `/base/one-segment`
 * (no per-value URI encoding).
 */
export function buildListingCanonicalPath(
  base: string,
  segments: string[],
): string {
  const b = base.startsWith("/") ? base : `/${base}`;
  if (segments.length === 0) return b;
  return `${b}/${segments.join("/")}`;
}

export function parseListingSegmentsFromPathname(
  pathname: string,
  base: string,
): string[] {
  const canon = pathnameToCanonical(pathname);
  const b = base.startsWith("/") ? base : `/${base}`;
  if (canon === b) return [];
  if (!canon.startsWith(`${b}/`)) return [];
  const rest = canon.slice(b.length + 1);
  if (!rest) return [];
  return rest.split("/").map((seg) => decodeURIComponentSafe(seg));
}

export type ListingUrlParts = {
  canonicalPath: string;
  queryString: string;
};

/** Preserves only allowed search keys (e.g. `q`) when syncing listing URLs. */
export function listingSearchParamsPreserved(
  searchParams: URLSearchParams,
  preserveKeys: string[],
): URLSearchParams {
  const next = new URLSearchParams();
  for (const key of preserveKeys) {
    const v = searchParams.get(key);
    if (v !== null && v !== "") next.set(key, v);
  }
  return next;
}

export function buildLocalizedListingUrl(
  locale: Locale,
  canonicalListingPath: string,
  searchParams: URLSearchParams,
  preserveKeys: string[],
): string {
  const qs = listingSearchParamsPreserved(searchParams, preserveKeys).toString();
  return withLocalePrefix(
    locale,
    qs ? `${canonicalListingPath}?${qs}` : canonicalListingPath,
  );
}
